import { getLocalStorage, sleep } from "@elit-hair/elit-tools";
import { captureException } from "@sentry/react";
import { Call, Device } from "@twilio/voice-sdk";
import { isPossiblePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

import { callSvcAxiosInstance } from "@/constants";
import { AUDIO_CONSTRAINTS } from "@/constants/audio";
import { CALL_STATUS } from "@/constants/call";
import { callServiceEndpoints } from "@/constants/endpoints";
import { LOCAL_STORAGE_KEYS } from "@/constants/local-storage-keys";
import { browserAudioLevelService } from "@/features/audio/adapters/browser/browser-audio-level.service";
import {
    mapTwilioDisconnectStatusToFailureType,
    mapTwilioErrorToFailureType,
} from "@/features/telephony/adapters/twilio/twilio-error.mapper";
import {
    mapTwilioCallStateToDomain,
    mapTwilioDisconnectStatusToDomain,
} from "@/features/telephony/adapters/twilio/twilio-status.mapper";
import {
    ConnectParams,
    ProviderConfig,
    TelephonyProvider,
} from "@/features/telephony/interfaces/telephony-provider.interface";
import { CallState } from "@/features/telephony/types/call-state.types";
import { TelephonyEventType } from "@/features/telephony/types/telephony-events.types";
import { TelephonyEvent } from "@/features/telephony/types/telephony-events.types";
import { audioProcessingService } from "@/services/audio-processing-service";
import { CallFailureType } from "@/types/call.types";
import { isIosDevice } from "@/utils/is-ios-device";
import { getVoiceRegistrationToken } from "@/utils/voice-registration.utils";

type Listener = (event: TelephonyEvent) => void;

export class TwilioAdapter implements TelephonyProvider {
    readonly id = "twilio" as const;

    private device: Device | undefined;
    private call: Call | undefined;
    private audioLevelCleanup: (() => void) | null = null;
    private listeners = new Set<Listener>();
    private visibilityHandler: (() => void) | null = null;
    private onlineHandler: (() => void) | null = null;
    private tokenRefreshInterval: ReturnType<typeof setInterval> | null = null;
    private microphoneReleasePromise: Promise<void> | null = null;

    public initialized = false;

    subscribe(listener: (event: TelephonyEvent) => void): () => void {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private emit(event: TelephonyEvent): void {
        this.listeners.forEach((listener) => listener(event));
    }

    async initialize(config: ProviderConfig): Promise<void> {
        if (this.initialized) {
            return;
        }

        const token = await this.fetchToken(config.registrationToken);

        audioProcessingService.applyIntercept();

        const device = new Device(token, {
            codecPreferences: [Call.Codec.Opus, Call.Codec.PCMU],
            appName: "Elitphone",
            closeProtection: true,
            tokenRefreshMs: 180000,
        });

        this.device = device;
        await device.audio?.setAudioConstraints(AUDIO_CONSTRAINTS);
        this.setupDeviceListeners();
        this.setOutgoingCallMelodyMuted(config.outgoingCallMelodyMuted ?? false);

        this.initialized = true;
        this.emit({ type: TelephonyEventType.PROVIDER_INITIALIZED, providerId: this.id });
    }

    async connect(params: ConnectParams): Promise<string> {
        if (!this.device) {
            throw new Error("Twilio provider not initialized");
        }

        if (!params.To) {
            throw new Error("Called number not provided");
        }

        const valid = isValidPhoneNumber(params.To);
        const isPossibleToCall = isPossiblePhoneNumber(params.To);

        if (!valid || !isPossibleToCall) {
            this.emit({
                type: TelephonyEventType.CALL_FAILED,
                sessionId: params.callId,
                failureType: CallFailureType.INVALID_NUMBER,
                state: CALL_STATUS.FAILED,
            });
            throw new Error("Invalid phone number");
        }

        const parsedPayload: Record<string, string> = {};
        for (const key in params) {
            const value = params[key as keyof ConnectParams];
            if (value) {
                parsedPayload[key] = value.toString();
            }
        }

        const storedDeviceId = getLocalStorage<string | null>(
            LOCAL_STORAGE_KEYS.AUDIO_DEVICE_MANAGEMENT.INPUT_DEVICE.key,
            null
        );
        if (storedDeviceId && this.device.audio && !isIosDevice()) {
            await this.device.audio.setInputDevice(storedDeviceId);
        }

        this.emit({
            type: TelephonyEventType.CALL_CONNECTING,
            sessionId: params.callId,
            state: CALL_STATUS.CONNECTING,
        });

        this.call = await this.device.connect({ params: parsedPayload });
        this.attachCallListeners(params.callId);

        const initialState = mapTwilioCallStateToDomain(this.call.status());
        this.emitCallStateEvent(params.callId, initialState);

        return params.callId;
    }

    async disconnect(): Promise<void> {
        if (!this.call) {
            await this.releaseMicrophone();
            return;
        }

        const activeCall = this.call;

        await new Promise<void>((resolve) => {
            const finish = () => {
                void this.releaseMicrophone(activeCall).finally(resolve);
            };

            if (activeCall.status() === Call.State.Closed) {
                finish();
                return;
            }

            activeCall.once("disconnect", finish);
            activeCall.disconnect();
        });
    }

    async mute(muted = true): Promise<boolean> {
        if (!this.call) {
            return false;
        }

        this.call.mute(muted);
        const actualStatus = this.call.isMuted();

        if (this.call) {
            this.emit({
                type: TelephonyEventType.CALL_MUTE_CHANGED,
                sessionId: "",
                muted: actualStatus,
            });
        }

        return actualStatus === muted;
    }

    isMuted(): boolean {
        return this.call?.isMuted() ?? false;
    }

    sendDigits(digits: string): void {
        this.call?.sendDigits(digits);
    }

    async setInputDevice(deviceId: string): Promise<void> {
        if (!this.device?.audio || isIosDevice()) {
            return;
        }

        await this.device.audio.setInputDevice(deviceId);
    }

    async setOutputDevice(deviceId: string): Promise<void> {
        if (!this.device?.audio || isIosDevice()) {
            return;
        }

        const twilioDevices = Array.from(this.device.audio.speakerDevices.get());
        if (!twilioDevices || twilioDevices.length === 0) {
            await sleep(1000);
        }

        this.device.audio.speakerDevices.set(deviceId);
    }

    async releaseMicrophoneAccess(): Promise<void> {
        await this.releaseMicrophone(this.call);
    }

    async setEchoCancellation(enabled: boolean): Promise<void> {
        if (!this.device?.audio) {
            return;
        }

        await this.device.audio.setAudioConstraints({
            ...AUDIO_CONSTRAINTS,
            echoCancellation: enabled,
        });
    }

    setOutgoingCallMelodyMuted(muted: boolean): void {
        this.device?.audio?.outgoing(!muted);
    }

    startAudioLevelMonitoring(onLevels: (levels: number[]) => void): (() => void) | null {
        if (!this.call || this.call.isMuted()) {
            return null;
        }

        let twilioStream: MediaStream | null | undefined;
        try {
            twilioStream = this.call.getLocalStream();
        } catch {
            twilioStream = null;
        }

        if (twilioStream && twilioStream.getAudioTracks().length > 0) {
            this.audioLevelCleanup = browserAudioLevelService.monitorStream(twilioStream, onLevels, () =>
                this.isMuted()
            );
        } else if (!isIosDevice()) {
            this.audioLevelCleanup = browserAudioLevelService.monitorFromUserMedia(onLevels, () => this.isMuted());
        } else {
            return null;
        }

        return () => {
            this.audioLevelCleanup?.();
            this.audioLevelCleanup = null;
        };
    }

    parseProviderStatus(status: unknown) {
        return mapTwilioCallStateToDomain(status as Call.State | undefined);
    }

    mapFailureType(error: unknown): CallFailureType {
        return mapTwilioErrorToFailureType(error);
    }

    async destroy(): Promise<void> {
        await this.disconnect();
        this.device?.destroy();
        this.device = undefined;
        this.initialized = false;
        this.teardownLifecycleListeners();
        this.emit({ type: TelephonyEventType.PROVIDER_DESTROYED, providerId: this.id });
    }

    private async fetchToken(fcmToken: string): Promise<string> {
        const response = await callSvcAxiosInstance.post(callServiceEndpoints.subscribe, {
            fcmToken,
        });
        return response.data.token;
    }

    private setupDeviceListeners(): void {
        if (!this.device) {
            return;
        }

        this.device.on("registered", () => {
            console.info("Twilio device is registered and ready for calls");
        });

        this.device.on("error", (error) => {
            console.error("Twilio device error:", error);
        });

        this.device.on("tokenWillExpire", () => {
            void this.refreshToken();
        });

        this.device.on("tokenExpired", () => {
            void this.refreshToken();
        });

        this.device.on("unregistered", () => {
            void this.refreshToken();
        });

        this.device.on("disconnect", () => {
            this.call = undefined;
        });

        this.visibilityHandler = () => {
            if (document.visibilityState === "visible") {
                void this.refreshToken();
            }
        };
        document.addEventListener("visibilitychange", this.visibilityHandler);

        this.onlineHandler = () => {
            void this.refreshToken();
        };
        window.addEventListener("online", this.onlineHandler);

        this.tokenRefreshInterval = setInterval(() => {
            void this.refreshToken();
        }, 1800000);
    }

    private teardownLifecycleListeners(): void {
        if (this.visibilityHandler) {
            document.removeEventListener("visibilitychange", this.visibilityHandler);
            this.visibilityHandler = null;
        }
        if (this.onlineHandler) {
            window.removeEventListener("online", this.onlineHandler);
            this.onlineHandler = null;
        }
        if (this.tokenRefreshInterval) {
            clearInterval(this.tokenRefreshInterval);
            this.tokenRefreshInterval = null;
        }
    }

    private async refreshToken(): Promise<void> {
        const registrationToken = getVoiceRegistrationToken(
            getLocalStorage<string>(LOCAL_STORAGE_KEYS.FCM_TOKEN.key, LOCAL_STORAGE_KEYS.FCM_TOKEN.default)
        );
        if (!registrationToken) {
            throw new Error("Voice registration token not found");
        }

        const newToken = await this.fetchToken(registrationToken);
        this.device?.updateToken(newToken);
    }

    private attachCallListeners(sessionId: string): void {
        if (!this.call) {
            return;
        }

        this.call.on("error", (error) => {
            const failedCall = this.call;
            this.call = undefined;
            void this.releaseMicrophone(failedCall);
            const state = CALL_STATUS.FAILED;
            this.emit({
                type: TelephonyEventType.CALL_FAILED,
                sessionId,
                failureType: mapTwilioErrorToFailureType(error),
                state,
            });
            this.emitCallStateEvent(sessionId, state);
            this.handleError(error, sessionId);
        });

        this.call.on("connect", () => {
            this.emitCallStateEvent(sessionId, mapTwilioCallStateToDomain(this.call?.status()));
        });

        this.call.on("reconnected", () => {
            this.emitCallStateEvent(sessionId, CALL_STATUS.CONNECTED);
        });

        this.call.on("reconnecting", () => {
            this.emitCallStateEvent(sessionId, CALL_STATUS.CONNECTING);
        });

        this.call.on("ringing", () => {
            this.emit({
                type: TelephonyEventType.CALL_RINGING,
                sessionId,
                state: mapTwilioCallStateToDomain(this.call?.status()),
            });
            this.emitCallStateEvent(sessionId, mapTwilioCallStateToDomain(this.call?.status()));
        });

        this.call.on("reject", () => {
            const rejectedCall = this.call;
            const state = mapTwilioCallStateToDomain(rejectedCall?.status());
            this.call = undefined;
            void this.releaseMicrophone(rejectedCall);
            this.emit({
                type: TelephonyEventType.CALL_FAILED,
                sessionId,
                failureType: CallFailureType.REJECTED,
                state,
            });
            this.emitCallStateEvent(sessionId, state);
            this.handleError("Call rejected", sessionId, rejectedCall);
        });

        this.call.on("cancel", () => {
            const cancelledCall = this.call;
            const state = mapTwilioCallStateToDomain(cancelledCall?.status());
            this.call = undefined;
            void this.releaseMicrophone(cancelledCall);
            this.emit({
                type: TelephonyEventType.CALL_FAILED,
                sessionId,
                failureType: CallFailureType.REJECTED,
                state,
            });
            this.emitCallStateEvent(sessionId, state);
            this.handleError("Call cancelled", sessionId, cancelledCall);
        });

        this.call.on("disconnect", () => {
            const disconnectedCall = this.call;
            const finalStatus = disconnectedCall?.status();
            const failureType = mapTwilioDisconnectStatusToFailureType(finalStatus);
            const state = mapTwilioDisconnectStatusToDomain(finalStatus);

            if (failureType) {
                this.emit({
                    type: TelephonyEventType.CALL_FAILED,
                    sessionId,
                    failureType,
                    state,
                });
                this.handleError(`Call ${failureType}`, sessionId, disconnectedCall);
            } else {
                this.emit({
                    type: TelephonyEventType.CALL_DISCONNECTED,
                    sessionId,
                    state,
                });
            }

            this.emitCallStateEvent(sessionId, state);
            this.call = undefined;
            void this.releaseMicrophone(disconnectedCall);
        });
    }

    private releaseMicrophone(call?: Call): Promise<void> {
        if (this.microphoneReleasePromise) {
            return this.microphoneReleasePromise;
        }

        this.microphoneReleasePromise = this.doReleaseMicrophone(call).finally(() => {
            this.microphoneReleasePromise = null;
        });

        return this.microphoneReleasePromise;
    }

    private async doReleaseMicrophone(call?: Call): Promise<void> {
        this.audioLevelCleanup?.();
        this.audioLevelCleanup = null;

        try {
            const localStream = call?.getLocalStream();
            localStream?.getTracks().forEach((track) => {
                if (track.readyState !== "ended") {
                    track.stop();
                }
            });
        } catch {
            // Call may already be torn down.
        }

        audioProcessingService.releaseCallStreams();

        try {
            await this.device?.audio?.unsetInputDevice();
        } catch (error) {
            console.warn("Failed to release microphone:", error);
        }
    }

    private emitCallStateEvent(sessionId: string, state: CallState): void {
        if (state === CALL_STATUS.CONNECTING || state === CALL_STATUS.QUEUED) {
            this.emit({ type: TelephonyEventType.CALL_CONNECTING, sessionId, state });
            return;
        }

        if (state === CALL_STATUS.RINGING) {
            this.emit({ type: TelephonyEventType.CALL_RINGING, sessionId, state });
            return;
        }

        if (state === CALL_STATUS.IN_PROGRESS || state === CALL_STATUS.CONNECTED) {
            this.emit({ type: TelephonyEventType.CALL_CONNECTED, sessionId, state });
            return;
        }

        this.emit({ type: TelephonyEventType.CALL_DISCONNECTED, sessionId, state });
    }

    private handleError(error: unknown, callId: string, call?: Call): void {
        captureException(error, {
            tags: {
                callId,
                callData: JSON.stringify(call),
            },
        });
    }
}

export const twilioAdapter = new TwilioAdapter();
